import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { NoticeAdHeader } from "../RaiseNoticeAdRequest/components/NoticeAdHeader";
import {
  raiseNoticeRequestSteps,
  StepKeysName,
  updateStepsWithLastStep,
} from "../RaiseNoticeAdRequest/utils";

import activeStepIcon from "../../assets/svg/ActiveAgencyIcon.svg";
import { RaiseNoticeRequestStep } from "../RaiseNoticeAdRequest/type";
import { useParams } from "react-router-dom";
import "./index.scss";
import { QuoteRequestForm } from "./components/QuoteRequestForm";
import { QuoteDetailsForm } from "./components/QuoteDetailsForm";
import { MarketingApprovalForm } from "./components/MarketingApprovalForm";
import { AdReleaseForm } from "./components/AdReleaseForm";
import { FinalMarketingForm } from "./components/FinalMarketingForm";
import { UploadProofForm } from "./components/UploadProofForm";
import { useDispatch, useSelector } from "react-redux";
import { RaiseAdRequestState } from "../../store/types/raiseAdRequest";
import { getUrlProperties } from "../../utilities/common-function";
import {
  getUserWorkspaceRoleAction,
  setRequestId,
} from "../../store/actions/raiseAdRequest";
import { ReviewAndCloseForm } from "./components/ReviewAndCloseForm";
import { FullScreenSpinner } from "../../components/shared/Spinner";
import { RequestCreationEmailForm } from "./components/RequestCreationEmailForm";

export const RaiseRequestWorkflow: FC = () => {
  const dispatch = useDispatch();
  const { lastStepName, loading, isFileUploading, userRole } = useSelector(
    (state: { raiseAdRequest: RaiseAdRequestState }) => state.raiseAdRequest
  );

  const { requestId, selectedProductId } = useParams<{
    requestId: string;
    selectedProductId: string;
  }>();

  const { baseUrl } = getUrlProperties();
  const currentStepKey = useMemo(() => {
    // Update only the stepKey in the URL
    const urlParts = baseUrl.split("/");
    return urlParts[8];
  }, [baseUrl]);

  const [steps, setSteps] = useState(raiseNoticeRequestSteps);
  const currentStep = useMemo(() => {
    return steps.find((step) => step.isCurrentlyActive) || null;
  }, [steps]);

  useEffect(() => {
    dispatch(setRequestId({ requestId, selectedProductId }));
    dispatch(getUserWorkspaceRoleAction(selectedProductId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentStepKey) return;
    const updatedSteps = updateStepsWithLastStep(lastStepName, currentStepKey, userRole);

    setSteps(updatedSteps);
  }, [currentStepKey, lastStepName, userRole]);

  const handleStepChange = useCallback(
    (clickedStep: RaiseNoticeRequestStep) => {
      if(!clickedStep.permittedRoles?.includes(userRole)){
              return;
      }
      setSteps((prevSteps) =>
        prevSteps.map((step) => ({
          ...step,
          isCurrentlyActive: step.stepKey === clickedStep.stepKey,
        }))
      );

      // Update only the stepKey in the URL
      const urlParts = window.location.hash.split("/"); // hash-based routing
      if (urlParts.length >= 5) {
        urlParts[5] = clickedStep.stepKey; // Replace the stepKey
        const newUrl = urlParts.join("/");
        window.history.pushState({}, "", newUrl);
      }
    },
    [userRole]
  );

  const renderCurrentStep = useMemo(() => {
    switch (currentStep?.stepKey) {
      case StepKeysName.RequestCreation:
        return <QuoteRequestForm />;
      case StepKeysName.QuotationAndApproval:
        return <RequestCreationEmailForm />;
      case StepKeysName.AgencyQuoteDetails:
        return <QuoteDetailsForm />;
      case StepKeysName.MarketingApprovals:
        return <MarketingApprovalForm />;
      case StepKeysName.AdRelease:
        return <AdReleaseForm />;
      case StepKeysName.FinalMarketGoLive:
        return <FinalMarketingForm />;
      case StepKeysName.UploadProof:
        return <UploadProofForm />;
      case StepKeysName.ReviewAndClose:
        return <ReviewAndCloseForm />;
      default:
        break;
    }
  }, [currentStep]);

  return (
    <>
      <NoticeAdHeader />
      {(loading || isFileUploading) && <FullScreenSpinner />}
      <div
        style={{
          display: "flex",
          height: "calc(100vh - 7rem)", // adjust based on header height
        }}
      >
        {/* Left Section - 25% */}
        <div
          style={{
            flex: "0 0 20%",
            backgroundColor: "#f8f9fa",
            borderRight: "2px solid #E0E3E5",
            background: "#fff",
          }}
        >
          {steps.map((step, index) => (
            <div
              key={step.stepKey}
              style={{
                padding: "1rem 1.5rem",
                background: !!step.isCurrentlyActive ? "#F2E5FD" : "#fff",
                opacity: step.isDisabled ? 0.5 : 1,
                pointerEvents: step.isDisabled ? "none" : "auto",
                cursor: step.isDisabled ? "not-allowed" : "pointer",
              }}
              onClick={() => handleStepChange(step)}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div
                    style={{
                      gap: "1rem",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        color: "#5C6674",
                        fontSize: "1.2rem",
                        fontWeight: 400,
                      }}
                    >
                      {" "}
                      Step {step.stepNumber}
                    </div>
                    <div
                      style={{
                        color: "#394455",
                        fontSize: "1.3rem",
                        fontWeight: 600,
                      }}
                    >
                      {step.name}
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "1rem 0",
                      color: "#5C6674",
                      fontSize: "1.2rem",
                      fontWeight: 400,
                    }}
                  >
                    ({step.allowedRoles})
                  </div>
                </div>
                {step.isCurrentlyActive ? (
                  <img src={activeStepIcon} alt="Active Step" />
                ) : (
                  <></>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Right Section - 75% */}
        <div
          style={{
            flex: 1,
            padding: "1rem 2rem",
            overflowY: "auto",
          }}
        >
          <div className="notice-header">
            <div className="notice-header__content">
              <div className="notice-header__info">
                <div className="notice-header__name">{currentStep?.name}</div>
              </div>
            </div>
          </div>

          {renderCurrentStep}
        </div>
      </div>
    </>
  );
};
